/**
 * A match of «Сучасна війна», as the server keeps it.
 *
 * The rules are in core/world/war.ts and are shared with the page; what is
 * here is the state of *one* match: which teams were made, who is on them,
 * how far the clock has run, whose flag is down and what everybody has done
 * to each other. None of it is saved - a match is an evening, and a server
 * that came back up in the middle of one with half a scoreboard would be
 * worse than one that came back up between matches.
 */
import {
  MAX_TEAMS, PREP_SECONDS, warClockAt, matchOver, winnerOf, standings,
  flagFor, spawnFor,
  type WarPhase, type WarTeam, type WarScore,
} from "../world/war.js";
import { flagId, PIRATE_FLAG } from "../block/registry.js";
import { WAR_GROUND, World } from "../world/world.js";
import { CHUNK_SIZE } from "../world/chunk.js";

interface Tally { kills: number; deaths: number }

/**
 * How long a match may last, in hours: three unless its maker says, and no
 * more than twelve. "Гра має закінчуватись автоматично через, наприклад,
 * 3 години, але нехай це буде налаштуванням гри перед запуском, де 12 -
 * найбільша кількість годин."
 */
export const MATCH_HOURS = 3;
export const MAX_MATCH_HOURS = 12;

export class WarMatch {
  private clock = 0;
  /** Seconds since the first beat, for the limit. */
  private age = 0;
  /** Over because the time ran out, rather than because flags fell. */
  private timeUp = false;
  private phase: WarPhase = "lobby";
  private readonly made: { id: number; name: string; bots: boolean;
    members: string[]; alive: boolean }[] = [];
  private readonly tally = new Map<string, Tally>();
  /** The team each name is on, so a page can be told "you". */
  private readonly onTeam = new Map<string, number>();

  constructor(
    /** "Час на підготовку задає створювач гри." */
    readonly prep: number = PREP_SECONDS,
    /** "Той хто створює гру вибирає чекбоксом «проти ботів»." */
    readonly bots: boolean = false,
    /** Хардкор: ten скринь of supplies by every team's прапор. */
    readonly hardcore: boolean = false,
    /**
     * Seconds from the world's first beat to its end, lobby included - a
     * lobby nobody ever started is a world that should go too. At the end
     * the match is over whatever the flags say, and the world is taken down.
     */
    readonly limit: number = MATCH_HOURS * 3600,
  ) {
    if (bots) {
      // One team of people and one of bots, and the teams exist before
      // anybody arrives: there is nothing to arrange in a bot match.
      this.made.push({ id: 0, name: "Наші", bots: false, members: [], alive: true });
      this.made.push({ id: 1, name: "Боти", bots: true, members: [], alive: true });
    }
  }

  get running(): boolean { return this.phase === "prepare" || this.phase === "fight"; }
  get inLobby(): boolean { return this.phase === "lobby"; }
  /** The wall is down and anybody can be anywhere. */
  get fighting(): boolean { return this.phase === "fight"; }
  get over(): boolean { return this.phase === "over"; }
  get teams(): readonly WarTeam[] { return this.made; }

  /** The owner makes a team: "нажимає створити команду і вводить назву". */
  make(name: string): boolean {
    if (!this.inLobby || this.bots) return false;
    if (this.made.length >= MAX_TEAMS) return false;
    const trimmed = name.trim().slice(0, 24);
    if (trimmed.length === 0) return false;
    if (this.made.some((t) => t.name === trimmed)) return false;
    this.made.push({
      id: this.made.length, name: trimmed, bots: false, members: [], alive: true,
    });
    return true;
  }

  /** Anybody joins one: "тепер команду бачать інші гравці і можуть доєднатись". */
  join(team: number, who: string): boolean {
    if (!this.inLobby) return false;
    const found = this.made[team];
    if (found === undefined || found.bots) return false;
    for (const t of this.made) {
      const at = t.members.indexOf(who);
      if (at >= 0) t.members.splice(at, 1);
    }
    found.members.push(who);
    this.onTeam.set(who, team);
    return true;
  }

  /** Which team somebody is on, or -1. */
  teamOf(who: string): number {
    return this.onTeam.get(who) ?? -1;
  }

  /**
   * Can the match start?
   *
   * Two teams with somebody on them, or a bot match with one person in it.
   * A match that can begin with one team is a match that ends the instant it
   * begins, since one flag standing is the win.
   */
  canBegin(): boolean {
    if (!this.inLobby) return false;
    const manned = this.made.filter((t) => t.bots || t.members.length > 0);
    return manned.length >= 2;
  }

  /**
   * "Створювач нажимає «почати»": the walls go up and the clock starts.
   *
   * Teams nobody joined are dropped first, and the ones that are left are
   * renumbered - the quarters are numbered from nought and a gap in them
   * would leave an army in a corner of the map with no flag in it.
   */
  begin(world: World): readonly WarTeam[] {
    if (!this.canBegin()) return [];
    const kept = this.made.filter((t) => t.bots || t.members.length > 0);
    this.made.length = 0;
    kept.forEach((t, i) => {
      this.made.push({ ...t, id: i });
      for (const who of t.members) this.onTeam.set(who, i);
    });
    world.raiseWar(this.made.length,
      (team) => this.made[team]?.bots === true ? PIRATE_FLAG : flagId(team),
      (team) => this.hardcore && this.made[team]?.bots !== true);
    this.phase = "prepare";
    this.clock = 0;
    return this.made;
  }

  /**
   * A tick of the match.
   *
   * @returns "opened" on the one tick where the preparing runs out, so the
   * caller can turn the wall to wood and tell everybody about the blocks.
   */
  tick(dt: number): "opened" | null {
    if (this.phase !== "over") {
      this.age += dt;
      if (this.age >= this.limit) {
        this.phase = "over";
        this.timeUp = true;
        return null;
      }
    }
    if (this.phase !== "prepare" && this.phase !== "fight") return null;
    const before = warClockAt(this.clock, this.prep).phase;
    this.clock += dt;
    const now = warClockAt(this.clock, this.prep);
    this.phase = now.phase;
    return before === "prepare" && now.phase === "fight" ? "opened" : null;
  }

  /**
   * Seconds left of the preparing - or, in the lobby, how long it will be.
   *
   * The lobby answers with the whole of it on purpose: "скільки я матиму
   * часу" is the first thing anybody asks before pressing «Почати», and
   * until this it could only be answered by starting the match.
   */
  get left(): number {
    if (this.phase === "prepare") {
      return Math.max(0, warClockAt(this.clock, this.prep).left);
    }
    return this.phase === "lobby" ? this.prep : 0;
  }

  /**
   * A прапор has come down.
   *
   * @returns whether that ended the match.
   */
  flagDown(team: number): boolean {
    const found = this.made[team];
    if (found === undefined || !found.alive) return false;
    found.alive = false;
    if (matchOver(this.made)) {
      this.phase = "over";
      return true;
    }
    return false;
  }

  /** Somebody died, and somebody may have killed them. */
  died(who: string, by: string | null): void {
    this.scoreFor(who).deaths++;
    if (by !== null && by !== who) this.scoreFor(by).kills++;
  }

  private scoreFor(who: string): Tally {
    const found = this.tally.get(who);
    if (found !== undefined) return found;
    const made = { kills: 0, deaths: 0 };
    this.tally.set(who, made);
    return made;
  }

  /** Where this team wakes up, in blocks. */
  spawn(team: number, world: World): { x: number; y: number; z: number } {
    const span = world.opts.sizeXZ * CHUNK_SIZE;
    return spawnFor(team, this.made.length, span, WAR_GROUND);
  }

  /** Where this team's прапор stands. */
  flagAt(team: number, world: World): { x: number; z: number } {
    const span = world.opts.sizeXZ * CHUNK_SIZE;
    return flagFor(team, this.made.length, span);
  }

  /** The winner, once there is one. */
  get winner(): WarTeam | null { return winnerOf(this.made); }

  /** What a page is told, from the point of view of one player. */
  state(who: string, owner: boolean): object {
    const scores: WarScore[] = [...this.tally.entries()].map(([name, t]) => ({
      name, team: this.onTeam.get(name) ?? -1, kills: t.kills, deaths: t.deaths,
    }));
    return {
      t: "war",
      phase: this.phase,
      left: Math.round(this.left),
      teams: this.made.map((t) => ({
        id: t.id, name: t.name, bots: t.bots,
        members: [...t.members], alive: t.alive,
      })),
      you: this.teamOf(who),
      owner,
      // Seconds until the world ends, counted down on every page.
      ends: Math.max(0, Math.round(this.limit - this.age)),
      ...(this.timeUp ? { timeUp: true } : {}),
      ...(this.hardcore ? { hardcore: true } : {}),
      ...(this.phase === "over"
        ? { scores: standings(scores, this.made) } : {}),
    };
  }
}
